import { Checkbox, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import type { Attachment, AttachmentInput } from 'sr-types/lib/production/v1/graphql';
import { DeepLinkItemHighlight } from '../deepLink';
import I18n from '../i18n/I18n';
import { H, V } from '../Layout';
import { useStyling } from '../Theme';
import AttachmentsRenderer from './AttachmentsRenderer';
import { normalizeTags } from './attachmentUtils';

type AttachmentsDisplayProps = {
    attachments: AttachmentInput[];
    deepLinkUIStatePath?: string;
    onDeleteAttachment?: (file: Attachment) => void;
    onEditAttachment?: (file: Attachment) => void;
    onShareAttachment?: (file: Attachment) => void;
    isPreview?: boolean;
    hideAction?: boolean;
    hasEntityCommentPermission?: boolean;
    enableSelection?: boolean;
    selectedAttachmentIds?: ReadonlySet<string>;
    disabledSelectedAttachmentIds?: ReadonlySet<string>;
    onToggleAttachmentSelection?: (attachmentId: string) => void;
};

export default (props: AttachmentsDisplayProps) => {
    const { theme } = useStyling();
    const {
        attachments = [],
        onDeleteAttachment,
        onEditAttachment,
        onShareAttachment,
        isPreview = false,
        hideAction = false,
        hasEntityCommentPermission = false,
        enableSelection = false,
        selectedAttachmentIds,
        disabledSelectedAttachmentIds,
        onToggleAttachmentSelection
    } = props;
    const selectedIds = useMemo<ReadonlySet<string>>(
        () => selectedAttachmentIds ?? new Set<string>(),
        [selectedAttachmentIds]
    );
    const disabledSelectedIds = useMemo<ReadonlySet<string>>(
        () => disabledSelectedAttachmentIds ?? new Set<string>(),
        [disabledSelectedAttachmentIds]
    );
    const showSelection = enableSelection && Boolean(onToggleAttachmentSelection);

    return (
        <V
            sx={{
                pl: 1,
                gap: 1,
                overflow: 'auto',
                height: 'fit-content',
                minHeight: 'fit-content',
                alignItems: 'stretch',
                width: '100%'
            }}
        >
            {attachments.length > 0 ? (
                attachments.map((file: Attachment, index: number) => {
                    const attachmentId = file.id;
                    const isDisabledSelected = Boolean(attachmentId && disabledSelectedIds.has(attachmentId));
                    return (
                        <DeepLinkItemHighlight
                            key={file.entityReference?.id || index}
                            id={file.entityReference?.id || ''}
                        >
                            <H sx={{ gap: 1, alignItems: 'center' }}>
                                {showSelection && attachmentId ? (
                                    <Checkbox
                                        checked={selectedIds.has(attachmentId) || isDisabledSelected}
                                        disabled={isDisabledSelected}
                                        onChange={() => {
                                            if (!isDisabledSelected) {
                                                onToggleAttachmentSelection?.(attachmentId);
                                            }
                                        }}
                                        inputProps={{ 'aria-label': file.name }}
                                        size="small"
                                    />
                                ) : null}
                                <V grow>
                                    <AttachmentsRenderer
                                        file={{
                                            ...file,
                                            tags: normalizeTags(file)
                                        }}
                                        onDelete={onDeleteAttachment ? () => onDeleteAttachment(file) : undefined}
                                        onEdit={onEditAttachment ? () => onEditAttachment(file) : undefined}
                                        onShare={onShareAttachment ? () => onShareAttachment(file) : undefined}
                                        isPreview={isPreview}
                                        hideAction={hideAction}
                                        hasEntityCommentPermission={hasEntityCommentPermission}
                                    />
                                </V>
                            </H>
                        </DeepLinkItemHighlight>
                    );
                })
            ) : (
                <H sx={{ justifyContent: 'center' }}>
                    <Typography color={theme.palette.text.disabled} variant="body2">
                        <I18n token="no.attachment.files.text" />
                    </Typography>
                </H>
            )}
        </V>
    );
};
